import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useShell } from '@/hooks/useShell';
import { DRAWER_ROLE_SWITCH, getDrawerAccordion } from '@/lib/navigation';
import { getRouteForRoleOrCategory } from '@/lib/roleRoutes';
import { cabinetNavigateOptions, homeNavigateOptions } from '@/lib/navigationState';
import { routes } from '@/lib/routes';
import type { RoleCode } from '@/types';

export const RoleDrawer = () => {
  const { t, locale } = useI18n();
  const { isDrawerOpen, closeDrawer, shell } = useShell();
  const { user, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const activeRole = user?.activeRole ?? null;
  const cabinet = shell?.roles.find((item) => item.key === activeRole) ?? null;
  const sections = getDrawerAccordion(activeRole);
  const [expanded, setExpanded] = useState<string>('work');

  useEffect(() => {
    if (isDrawerOpen) {
      closeRef.current?.focus();
      setExpanded('work');
      document.body.classList.add('r51-drawer-active');
    } else {
      document.body.classList.remove('r51-drawer-active');
    }
    return () => document.body.classList.remove('r51-drawer-active');
  }, [isDrawerOpen]);

  const roleTitle = cabinet
    ? cabinet.label[locale]
    : user
      ? t('drawer.role.anonymous')
      : t('drawer.role.anonymous');

  const onSwitchRole = (code: string) => {
    const canSwitch = Boolean(user?.roles.includes(code as RoleCode));
    if (canSwitch) switchRole(code as RoleCode);
    const href = getRouteForRoleOrCategory(code);
    if (href) {
      navigate(
        href,
        cabinetNavigateOptions(href, {
          role: code,
        }),
      );
    }
    closeDrawer();
  };

  return (
    <>
      <button
        type="button"
        className={`r51-mobile-overlay${isDrawerOpen ? ' is-visible' : ''}`}
        tabIndex={-1}
        aria-hidden={!isDrawerOpen}
        onClick={closeDrawer}
      />
      <aside
        className={`r51-mobile-drawer${isDrawerOpen ? ' is-open' : ''}`}
        aria-hidden={!isDrawerOpen}
        aria-modal={isDrawerOpen}
        role="dialog"
        aria-label={t('drawer.title')}
      >
        <div className="r51-mobile-drawer__header">
          <div>
            <strong>{t('drawer.title')}</strong>
            <small>{roleTitle}</small>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="r51-mobile-close"
            aria-label={t('a11y.closeMenu')}
            onClick={closeDrawer}
            tabIndex={isDrawerOpen ? 0 : -1}
          >
            ×
          </button>
        </div>

        {user ? (
          <div className="r51-mobile-role-switch" aria-label={t('drawer.roles')}>
            {DRAWER_ROLE_SWITCH.filter((role) => user.roles.includes(role.code as RoleCode)).map(
              (role) => (
                <button
                  key={role.code}
                  type="button"
                  className={activeRole === role.code ? 'active' : undefined}
                  onClick={() => onSwitchRole(role.code)}
                  tabIndex={isDrawerOpen ? 0 : -1}
                >
                  {role.label}
                </button>
              ),
            )}
          </div>
        ) : null}

        <nav className="r51-mobile-accordion">
          {sections.map((section) => {
            const isOpen = expanded === section.id;
            return (
              <section
                key={section.id}
                className={`r51-mobile-section${isOpen ? ' is-expanded' : ''}`}
              >
                <button
                  type="button"
                  className="r51-mobile-section__toggle"
                  aria-expanded={isOpen}
                  onClick={() => setExpanded(isOpen ? '' : section.id)}
                  tabIndex={isDrawerOpen ? 0 : -1}
                >
                  <span>{section.label}</span>
                  <span aria-hidden="true">⌄</span>
                </button>
                <div className="r51-mobile-section__panel">
                  {section.links.map((link) => (
                    <NavLink
                      key={link.id}
                      to={link.to}
                      end={link.to === '/' || link.to === '/app'}
                      className={({ isActive }) =>
                        `r51-mobile-screen${isActive ? ' active' : ''}`
                      }
                      onClick={(event) => {
                        if (link.to === routes.home) {
                          event.preventDefault();
                          navigate(
                            routes.home,
                            homeNavigateOptions(location.pathname, location.state),
                          );
                        }
                        closeDrawer();
                      }}
                      tabIndex={isDrawerOpen && isOpen ? 0 : -1}
                    >
                      <span>{link.label}</span>
                    </NavLink>
                  ))}
                </div>
              </section>
            );
          })}
        </nav>
      </aside>
    </>
  );
};
